/**
 * RX Assistant API service for frontend
 */
import apiClient from "../lib/api";

export interface QueryRequest {
  question: string;
  session_id?: number;
}

export interface VisualizationConfig {
  type: "card" | "line_chart" | "bar_chart" | "pie_chart" | "table" | "text";
  title: string;
  config: Record<string, any>;
}

export interface QueryResponse {
  query_id: number;
  session_id: number;
  question: string;
  sql: string;
  visualization: VisualizationConfig;
  results: Array<Record<string, any>>;
  row_count: number;
  execution_time_ms: number;
  success: boolean;
  error_message?: string;
  created_at: string;
}

export interface SessionSummary {
  id: number;
  title: string;
  created_at: string;
  updated_at: string;
  query_count: number;
}

export interface SessionDetail {
  id: number;
  title: string;
  created_at: string;
  updated_at: string;
  queries: QueryResponse[];
}

export interface SessionListResponse {
  sessions: SessionSummary[];
  total: number;
}

export interface FeedbackRequest {
  rating: number;
  comment?: string;
  issue_type?: string;
}

export const rxAssistantService = {
  /**
   * Send a question to RX Assistant
   */
  async sendQuery(request: QueryRequest): Promise<QueryResponse> {
    const response = await apiClient.post("/rx-assistant/query", request);
    return response.data;
  },

  /**
   * Send a question with SSE streaming
   */
  async sendQueryStream(
    request: QueryRequest,
    onEvent: (event: StreamEvent) => void
  ): Promise<void> {
    const token = localStorage.getItem("access_token");
    const API_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:8000";
    
    const response = await fetch(`${API_URL}/rx-assistant/query/stream`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body?.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    if (!reader) {
      throw new Error("No response body");
    }

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        
        // Process all complete lines
        // The last part might be incomplete, so we keep it in the buffer
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.trim() === "") continue;
          
          if (line.startsWith("data: ")) {
            const data = line.substring(6);
            try {
              const event = JSON.parse(data);
              onEvent(event);
            } catch (e) {
              console.error("Failed to parse SSE data:", e);
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  },

  /**
   * Get all user sessions
   */
  async getSessions(): Promise<SessionListResponse> {
    const response = await apiClient.get("/rx-assistant/sessions");
    return response.data;
  },

  /**
   * Get a specific session with all queries
   */
  async getSessionById(sessionId: number): Promise<SessionDetail> {
    const response = await apiClient.get(`/rx-assistant/sessions/${sessionId}`);
    return response.data;
  },

  /**
   * Delete (archive) a session
   */
  async deleteSession(sessionId: number): Promise<void> {
    await apiClient.delete(`/rx-assistant/sessions/${sessionId}`);
  },

  /**
   * Submit feedback for a query
   */
  async submitFeedback(
    queryId: number,
    feedback: FeedbackRequest
  ): Promise<void> {
    await apiClient.post(`/rx-assistant/queries/${queryId}/feedback`, feedback);
  },
};

export interface StreamEvent {
  type:
    | "thinking"
    | "session"
    | "generating_sql"
    | "sql_generated"
    | "executing"
    | "complete"
    | "error";
  message?: string;
  session_id?: number;
  sql?: string;
  query_id?: number;
  question?: string;
  visualization?: VisualizationConfig;
  results?: any[];
  row_count?: number;
  execution_time_ms?: number;
  success?: boolean;
  created_at?: string;
}
